import { Inject, Injectable } from '@angular/core'
import { ComponentStore } from '@ngrx/component-store'
import { ItemsApiService } from 'libs/gb-dashboard/item/data-access/src/lib/+state/items-api.service'
import {
  ItemsFilters,
  SlimItemsEntity,
  SlimItemsResponse,
} from 'libs/gb-dashboard/item/data-access/src/lib/+state/items.models'
import { tap, switchMap, withLatestFrom, debounceTime, filter } from 'rxjs/operators'
import { tapResponse } from '@ngrx/operators'
import { EnvironmentModel } from 'apps/gb-dashboard/src/environments/environment.model'
import { APP_CONFIG } from 'libs/shared/app-config/src/lib/gb-app-config'
import { HttpErrorResponse } from '@angular/common/http'
import { SelectedAuctionFacade } from 'libs/gb-dashboard/auctions/data-access-selected-auction/src/lib/+state/selected-auction.facade'
import { isEqual } from 'lodash'

interface State {
  results: SlimItemsEntity[]
  loading: boolean
  offset: number
  count: number
  filters: ItemsFilters
  error?: HttpErrorResponse
}

@Injectable()
export class GbItemSelectStore extends ComponentStore<State> {
  readonly loading$ = this.select((state) => state.loading)
  readonly count$ = this.select((state) => state.count)
  readonly items$ = this.select((state) => state.results)
  readonly search$ = this.select((state) => state.filters.search?.trim())

  constructor(
    private itemsApiService: ItemsApiService,
    private selectedAuctionFacade: SelectedAuctionFacade,
    @Inject(APP_CONFIG) private appConfig: EnvironmentModel,
  ) {
    super({ results: [], loading: false, offset: 0, count: 0, filters: {} })
  }

  readonly loadInitialItems = this.effect<ItemsFilters | void>((filters$) =>
    filters$.pipe(
      withLatestFrom(this.state$),
      tap(([filters, state]) => {
        // load only if no previous results OR filters changed
        if (!state.results.length || !isEqual(state.filters, filters ?? {})) {
          this.patchState({ filters: filters ?? {}, offset: 0 })
          this.getItems({ offset: 0 })
        }
      }),
    ),
  )

  readonly loadItemsNextPage = this.effect<void>((trigger$) =>
    trigger$.pipe(
      withLatestFrom(this.state$),
      filter(([, state]) => !state.loading && state.results.length < state.count),
      tap(([, state]) => {
        const offset = state.offset + this.appConfig.itemsPageSize
        this.getItems({ offset })
      }),
    ),
  )

  readonly searchItems = this.effect<string>((search$) =>
    search$.pipe(
      debounceTime(500),
      tap((search) => {
        this.patchState((state) => ({
          filters: { ...state.filters, search },
          loading: true,
        }))
        this.getItems({ offset: 0 })
      }),
    ),
  )

  private getItems = this.effect<{ offset: number }>((params$) =>
    params$.pipe(
      withLatestFrom(this.selectedAuctionFacade.urlName$, this.state$),
      switchMap(([{ offset }, urlName, state]) => {
        this.patchState({ loading: true, offset })

        return this.itemsApiService.getItems(urlName, offset, state.filters).pipe(
          tapResponse({
            next: (response: SlimItemsResponse) =>
              this.patchState((state) => ({
                results: offset === 0 ? response.results : [...state.results, ...response.results],
                count: response.count,
                loading: false,
                offset,
              })),
            error: (error: HttpErrorResponse) => this.patchState({ error, loading: false, offset }),
          }),
        )
      }),
    ),
  )
}
